import { useState, useEffect } from "react";
import { supabase } from "@/api/supabase"; // Ensure you have access to supabase here
import {
  NewsletterContext,
  NewsletterConfig,
  NewsletterContextType,
  NewsletterPreview,
} from "../context/NewsletterContext";
import { useAuth } from "@/auth/useAuth"; // Assuming you have a useAuth hook

const initialConfig: NewsletterConfig = {
  topics: [],
  tone: null,
  toneFeedback: "",
  frequency: null,
  preferredDay: "",
  testEmail: "",
  currentStep: 1,
  isActive: true,
};

const initialPreview: NewsletterPreview | null = null;

export const NewsletterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [config, setConfig] = useState<NewsletterConfig>(initialConfig);
  const [preview, setPreview] = useState<NewsletterPreview | null>(
    initialPreview
  );
  const [loading, setLoading] = useState(true);
  const { session } = useAuth();
  const userEmail = session?.user.user_metadata.email;

  useEffect(() => {
    const fetchConfig = async () => {
      if (!userEmail) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("newsletter_configs")
          .select("*")
          .eq("user_email", userEmail)
          .single();

        if (error) throw error;

        if (data) {
          setConfig({
            topics: data.topics,
            topicsEmbedding: data.topics_embedding,
            tone: data.tone,
            toneFeedback: data.tone_feedback,
            frequency: data.frequency,
            preferredDay: data.preferred_day,
            testEmail: data.testEmail,
            currentStep: data.currentStep,
            isActive: data.is_active,
          });

          // Set preview if it exists in the data
          if (
            data.sample_newsletter?.subject ||
            data.sample_newsletter?.body ||
            data.sample_articles
          ) {
            setPreview({
              subject: data.sample_newsletter?.subject || "",
              body: data.sample_newsletter?.body || "",
              articles: data.sample_articles || [],
            });
          }
        }
      } catch (error) {
        console.error("Error fetching newsletter config:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [userEmail]);

  if (loading) {
    return <div>Loading...</div>;
  }

  const value: NewsletterContextType = {
    config,
    preview,
    updateTopics: (topics: string[]) =>
      setConfig((prev) => ({ ...prev, topics })),
    updateTone: (tone, feedback) =>
      setConfig((prev) => ({ ...prev, tone, toneFeedback: feedback })),
    updateFrequency: ({ frequency, preferredDay }) =>
      setConfig((prev) => ({
        ...prev,
        frequency,
        preferredDay,
      })),
    updateIsActive: (isActive: boolean) => {
      setConfig((prev) => {
        const newConfig = { ...prev, isActive };
        console.log("State updated to:", newConfig.isActive);
        return newConfig;
      });
    },
    updatePreview: (newPreview) => {
      console.log("NewsletterProvider updatePreview:", newPreview);
      setPreview(newPreview);
    },
    isStepValid: (step: number) => {
      switch (step) {
        case 1:
          return config.topics.length > 0;
        case 2:
          return !!config.tone;
        case 3:
          return !!config.frequency;
        default:
          return false;
      }
    },
  };

  return (
    <NewsletterContext.Provider value={value}>
      {children}
    </NewsletterContext.Provider>
  );
};
